import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { navigate } from '@reach/router';
import uiActions from 'core/ui/actions';
import analyticsActions from 'core/analytics/actions';
import dialogActions from 'core/dialogs/actions';
import Layout from 'containers/Layout';
import Start from 'pages/Start';
import Default from 'pages/Default';
import ErrorPage from 'pages/Error';
import Home from 'pages/home/Home';
import Done from 'pages/Done';
import Kba from 'pages/Kba';
import Launch from 'pages/Launch';
import UserauthInternational from 'pages/UserauthInternational';
import SSOHome from 'pages/SSOHome';
import Signerstatus from 'pages/Signerstatus';
import GooglePlacesAutocompleteProvider from 'contexts/GooglePlacesAutocompleteContext';
import useScrollTop from 'hooks/useScrollTop';
import useScrollToErrors from 'hooks/useScrollToErrors';
import hasError from 'utils/hasError';
import Router from './Router';

const App = ({
  activeGroup,
  errorPage,
  hasFieldError,
  hasGlobalError,
  hasMultipleGroups,
  hasSessionMeta,
  initAnalytics,
  initApp,
  loading,
  menuOpen,
  page,
  reset,
  sessionMeta,
  timeoutMs,
  triggerTimeOutDialog,
}) => {
  const dynamicDonePages =
    (sessionMeta &&
      sessionMeta.configs &&
      sessionMeta.configs.dynamic_done_pages &&
      sessionMeta.configs.dynamic_done_pages.split(',')) ||
    [];

  const [hasNavigated, setHasNavigated] = useState(false);
  const pageNameRef = useRef(page.name);

  let Userauth = Default;
  if (hasMultipleGroups && page.name === 'userauth') {
    Userauth = UserauthInternational;
  }

  useEffect(() => {
    if (!page.name) {
      initAnalytics();
      initApp();
    }
  }, [initAnalytics, initApp, page.name]);

  useEffect(() => {
    pageNameRef.current = page.name;
    if (page.name) {
      navigate(`/oao/${page.name}`);
      window.history.pushState(null, document.title, window.location.href);
      setHasNavigated(true);
    }
  }, [page.name]);

  /**
   * The effect above only guards one back press per page, so pressing back
   * repeatedly can desync the URL from redux `page.name` and render the wrong
   * page. Force every popstate back to the page redux actually thinks we're on.
   */
  useEffect(() => {
    const handlePopState = () => {
      if (pageNameRef.current) {
        navigate(`/oao/${pageNameRef.current}`, { replace: true });
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (page.name !== 'userauth' && page.name !== 'error') {
        triggerTimeOutDialog();
      } else {
        clearTimeout(timeout);
      }
    }, timeoutMs);

    return () => {
      clearTimeout(timeout);
    };
  }, [timeoutMs, reset, triggerTimeOutDialog, page.name]);
  useEffect(() => {
    if (!hasSessionMeta && errorPage && !page.name) {
      const html = document.querySelector('html');
      html.innerHTML = errorPage;
    }
  }, [errorPage, hasSessionMeta, page.name]);
  // scroll to the top when the group or page change
  useScrollTop(activeGroup, page.name);
  // scroll to the error when an error occurs
  useScrollToErrors(hasFieldError, hasGlobalError, loading);
  // this should just go in the UI reducer
  if (!hasSessionMeta) {
    return null;
  }

  if (!page.name || !hasNavigated) {
    return <Layout>{null}</Layout>;
  }

  return (
    <Layout>
      <GooglePlacesAutocompleteProvider>
        <Router primary={false}>
          <Default path="autoexport" disableButton={hasFieldError || loading || menuOpen} />
          <Default path="beneficiaries" disableButton={hasFieldError || loading || menuOpen} />
          <Default path="documents" disableButton={hasFieldError || loading || menuOpen} />
          <Done path="done" disableButton={loading || menuOpen} />
          {dynamicDonePages.map((name) => (
            <Done key={name} path={name} disableButton={loading || menuOpen} />
          ))}
          <ErrorPage path="error" />
          <Default path="esign" disableButton={hasFieldError || loading || menuOpen} />
          <Default path="funding" disableButton={hasFieldError || loading || menuOpen} />
          <Default path="offers" disableButton={hasFieldError || loading || menuOpen} />
          <Kba path="kba" disableButton={hasFieldError || loading || menuOpen} />
          <Default path="kyc" disableButton={hasFieldError || loading || menuOpen} />
          <Default path="primaryinfo" disableButton={hasFieldError || loading || menuOpen} />
          <Default path="productoptions" disableButton={hasFieldError || loading || menuOpen} />
          <Default path="qualify" disableButton={hasFieldError || loading || menuOpen} />
          <Default path="secondaryinfo" disableButton={hasFieldError || loading || menuOpen} />
          <Default path="signerinfo" disableButton={hasFieldError || loading || menuOpen} />
          <Userauth path="userauth" disableButton={hasFieldError || loading || menuOpen} />
          <Start path="start" />
          <Launch path="launch" />
          <Home path="home" disableButton={loading || menuOpen} />
          <Signerstatus path="signerstatus" disableButton={hasFieldError || loading || menuOpen} />
          <SSOHome path="landing" />
          <Default path="login" disableButton={hasFieldError || loading || menuOpen} />
          <Default default disableButton={hasFieldError || loading || menuOpen} />
        </Router>
      </GooglePlacesAutocompleteProvider>
    </Layout>
  );
};

App.propTypes = {
  activeGroup: PropTypes.string,
  errorPage: PropTypes.string,
  hasFieldError: PropTypes.bool,
  hasGlobalError: PropTypes.bool,
  hasMultipleGroups: PropTypes.bool,
  hasSessionMeta: PropTypes.bool,
  initAnalytics: PropTypes.func.isRequired,
  initApp: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  menuOpen: PropTypes.bool,
  page: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  reset: PropTypes.number.isRequired,
  sessionMeta: PropTypes.shape({
    configs: PropTypes.shape({
      dynamic_done_pages: PropTypes.string,
    }),
  }),
  timeoutMs: PropTypes.number.isRequired,
  triggerTimeOutDialog: PropTypes.func.isRequired,
};

App.defaultProps = {
  activeGroup: null,
  errorPage: '',
  hasFieldError: false,
  hasGlobalError: false,
  hasMultipleGroups: false,
  hasSessionMeta: false,
  loading: false,
  menuOpen: false,
  sessionMeta: {},
};

const mapStateToProps = ({ fields, groups, page, refs, session, timeOut, ui }) => {
  const timeoutMs = timeOut.timeSeconds ? timeOut.timeSeconds * 1000 - 60000 : 840000;
  return {
    activeGroup: ui.activeGroup,
    errorPage: ui.errorPage,
    hasFieldError: hasError(fields),
    hasGlobalError: hasError(groups) || (page && page.error),
    hasMultipleGroups: Object.keys(groups).length > 1,
    hasSessionMeta: !!session.meta,
    loading: !!(ui.pageLoading || ui.fieldLoading || ui.appLoading || ui.groupLoading),
    menuOpen: ui.menuOpen,
    page,
    product: refs.product,
    reset: timeOut.reset,
    sessionMeta: session.meta,
    timeoutMs,
  };
};

const mapDispatchToProps = {
  initAnalytics: analyticsActions.analyticsInit,
  initApp: () => uiActions.init(),
  triggerTimeOutDialog: () => dialogActions.open('sessiontimeout'),
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
